import { useDialogContext } from "./controller/DialogProvider";
import React, { useEffect, useRef } from "react";
import { StyledDialogProvider } from "./controller/DialogOverlay";
import useModalStackStore from "store/useModalStackStore";
import { Util } from "Util";

interface DialogProps {
  children: React.ReactNode[];
  size: "large" | "small";
  state: "info" | "warning" | "success" | "error";
  zIndex?: string;
  preventClose?: boolean; // Esc를 통한 모달 닫기, close 아이콘 클릭을 통한 모달 닫기를 막아야 하는 상황에서 사용
}

export const Dialog = ({
  children,
  size,
  state,
  zIndex,
  preventClose = false,
}: DialogProps) => {
  const modalRef = useRef(null);
  const { closeDialog } = useDialogContext();
  const { addModal, removeModal, getTopModal } = useModalStackStore();

  const escEvent = (e: KeyboardEvent) => {
    if (preventClose) return;
    /** 최상위 모달이 현재 모달일 때만 esc 로 닫히게 */
    if (getTopModal() !== modalRef) {
      return false;
    }
    Util.handleKeyPress(e, "Escape", closeDialog);
  };

  useEffect(() => {
    addModal(modalRef);
    const onPressESCKeyCheck = (event: KeyboardEvent) => {
      escEvent(event);
    };

    document.addEventListener("keydown", onPressESCKeyCheck);
    return () => {
      document.removeEventListener("keydown", onPressESCKeyCheck);
      removeModal(modalRef);
    };
  }, []);

  useEffect(() => {
    return () => closeDialog();
  }, []);

  return (
    <StyledDialogProvider
      $size={size}
      $state={state}
      $zIndex={zIndex}
      $preventClose={preventClose}
    >
      <div className="container">
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            const childProps = { size, state, zIndex };

            return React.cloneElement(child, childProps);
          }
          return null;
        })}
      </div>
    </StyledDialogProvider>
  );
};
